import './Item.css';
import * as React from 'react';
import {
  getItemAmount,
  getItemLocation,
  getItemPosition,
  getItemPrice,
  getItemSize,
  getItemSrc,
  getItemType,
  isItemType,
} from '../resources/Items';
import { ItemInfo } from '../features/ItemInfo/ItemInfo';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Core, IItem, addItem, removeItemFromInventory, setItemPosition, swapItems, updateItem } from '../slices/inventorySlice';
import { dragItem } from '../slices/uiSlice';
import { ContextMenu } from './ContextMenu/ContextMenu';
import { ImageWithSpinner } from './ImageWithSpinner';
import { ItemType } from '../enums';
import { Spinner } from './Spinner';
import { CharacterContext } from '../context/CharacterContext';
import { CDNImage } from './Elements/Image';
import { closeDisplay, display, prompt } from '../util/EventEmitter';
import { toServer } from '../util/ServerSocket';
import { PetSkillBook } from '../features/PetSkillBook/PetSkillBook';
import { batch } from 'react-redux';
import { addStones } from '../slices/currencySlice';
import { useItemLongPress } from '../hooks/useItemLongPress';
import { ItemDetails } from '../features/ItemInfo/ItemDetails';

const OUTLINES: { [key: number]: string } = {
  0: 'rgba(255, 255, 255, 0.3)',
  1: 'rgba(30, 255, 0, 0.3)',
  2: 'rgba(0, 112, 221, 0.3)',
  3: 'rgba(163, 53, 238, 0.3)',
  4: 'rgba(255, 128, 0, 0.3)',
};

interface Props {
  item: IItem;
  onClick?: () => void;
  onSwap?: (uid: string) => void;
  style?: React.CSSProperties;
  noBackground?: boolean;
  perRow?: number;
  overridePosition?: number;
  readOnly?: boolean;
  tapMenu?: boolean;
  slotMenu?: boolean;
  onPurchase?: () => void;
}

const EQUIP_ITEM_TYPES = [
  ItemType.Avatar,
  ItemType.Weapon,
  ItemType.Gloves,
  ItemType.Pet,
  ItemType.Ring,
  ItemType.Amulet,
  ItemType.Helm,
  ItemType.Body,
  ItemType.Belt,
  ItemType.Shoes,
];

export const Item: React.FC<Props> = ({ item, onClick, onSwap, style = {}, noBackground = false, perRow = 10, overridePosition, readOnly = false, tapMenu = false, slotMenu = false, onPurchase }) => {
  const dispatch = useAppDispatch();
  const ref = React.useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [details, setDetails] = React.useState(false);
  const [showContextMenu, setShowContextMenu] = React.useState(false);
  const [showSpinner, setShowSpinner] = React.useState(false);
  const locations = useAppSelector((state) => state.inventory.locations);
  const draggedItem = useAppSelector((state) => state.ui.dragging.item);
  const { width, height } = getItemSize(item);
  const onPointerDown = useItemLongPress(tapMenu && !onPurchase && !readOnly && !showSpinner && draggedItem === null, () => {
    setShowContextMenu(false);
    setHover(false);
    setHidden(true);
    dispatch(dragItem(item));
  }, () => dispatch(dragItem(null)));
  React.useEffect(() => {
    if (!showContextMenu) return;
    const close = (event: Event) => {
      if (ref.current?.contains(event.target as Node) || (event.target as Element).closest?.('#context-menu')) return;
      setShowContextMenu(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [showContextMenu]);

  const locationCopy = JSON.parse(JSON.stringify(locations));
  Array(10)
    .fill(null)
    .forEach((_, idx) => delete locationCopy[idx]);

  const isEquipped = noBackground || Object.values(locationCopy).includes(item.uid);

  React.useEffect(() => {
    if (draggedItem === null) {
      setHidden(false);
    }
  }, [draggedItem]);

  let hoverTimeout: NodeJS.Timeout | null = null;
  const dropHandled = React.useRef(false);

  function _onClick(e: React.MouseEvent) {
    dropHandled.current = false;
    if (showSpinner) { e.stopPropagation(); return; }
    if (readOnly) { e.stopPropagation(); e.preventDefault(); return; }
    if (e.button === 2) {
      return;
    }

    if (onClick !== undefined && !(tapMenu && onPurchase)) {
      onClick();
    } else if (draggedItem === null) {
      if (tapMenu) {
        setHover(false);
        setShowContextMenu(value => !value);
      } else {
        setHidden(true);
        dispatch(dragItem(item));
      }
    } else {
      dropHandled.current = true;
      dispatch(dragItem(null));

      if (isItemType(draggedItem, ItemType.Gem) && [ItemType.Weapon, ItemType.Gloves, ItemType.Ring, ItemType.Amulet, ItemType.Helm, ItemType.Body, ItemType.Belt, ItemType.Shoes].includes(getItemType(item))) {
        toServer('gemInsert', { uid: item.uid, gemUID: draggedItem.uid });
      } else {
        dispatch(swapItems({ uid: item.uid, targetUID: draggedItem.uid }));
        toServer('swapItems', { itemUID: draggedItem.uid, targetUID: item.uid });
      }
    }

    e.stopPropagation();
    e.preventDefault();
  }

  function onMouseEnter() {
    if (!tapMenu) setHover(true);
  }

  function onMouseLeave() {
    setHover(false);
    if (!tapMenu) setShowContextMenu(false);
  }

  function onMouseUp() {
    if (dropHandled.current) { dropHandled.current = false; return; }
    if (readOnly) return;
    if (draggedItem === null) {
      return;
    }
    if (draggedItem.uid === item.uid) return;

    dispatch(dragItem(null));

    if (isItemType(draggedItem, ItemType.Gem) && [ItemType.Weapon, ItemType.Gloves, ItemType.Ring, ItemType.Amulet, ItemType.Helm, ItemType.Body, ItemType.Belt, ItemType.Shoes].includes(getItemType(item))) {
      toServer('gemInsert', { uid: item.uid, gemUID: draggedItem.uid });
    } else {
      dispatch(swapItems({ uid: item.uid, targetUID: draggedItem.uid }));
      toServer('swapItems', { itemUID: draggedItem.uid, targetUID: item.uid });
    }
  }

  function onContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    if (readOnly) return;
    hoverTimeout !== null && clearTimeout(hoverTimeout);
    setHover(false);
    setShowContextMenu(true);
  }

  function onSell() {
    const price = getItemPrice(item) * getItemAmount(item);

    setShowSpinner(true);

    prompt(
      `Sell ${getItemAmount(item)} [[item__${item.iid}--name]] for ${price}`,
      () => {
        toServer('sellItem', item.uid, () => {
          setHover(false);
          setShowContextMenu(false);
          setShowSpinner(false);
          batch(() => {
            dispatch(removeItemFromInventory(item.uid));
            dispatch(addStones(price));
          });
        });
      },
      () => {
        setHover(false);
        setShowContextMenu(false);
        setShowSpinner(false);
      }
    );
  }

  function onUse(e: React.MouseEvent, all = false) {
    e.stopPropagation();
    e.preventDefault();

    setShowSpinner(true);

    if (isItemType(item, ItemType.PetSkillBook)) {
      display(
        'petSkillBook',
        <PetSkillBook
          item={item}
          onClose={() => {
            setShowSpinner(false);
            closeDisplay('petSkillBook');
          }}
        />
      );
    } else {
      toServer('useItem', all ? { uid: item.uid, all: true } : item.uid, () => {
        setShowSpinner(false);
      });
    }

    setShowContextMenu(false);
  }

  function onSplit() {
    setShowSpinner(true);
    toServer('splitItem', { uid: item.uid }, ([newAmount, newItem]) => {
      const updatedItem = JSON.parse(JSON.stringify(item));

      updatedItem.core[Core.AMOUNT] = newAmount;

      dispatch(updateItem(updatedItem));
      dispatch(addItem(newItem));

      setShowSpinner(false);
    });
    setShowContextMenu(false);
  }

  const pos = overridePosition === undefined ? getItemPosition(item) : overridePosition;
  const x = pos % perRow;
  const y = Math.floor(pos / perRow);
  const src = getItemSrc(item);
  const props = item.props || { lines: [] };

  let _style: React.CSSProperties = {
    position: 'absolute',
    top: y * 27,
    left: x * 27,
    width: width * 26 + (width - 1),
    height: height * 26 + (height - 1),
    // border: isEquipped ? '' : `1px solid ${OUTLINES[item?.props?.lines.length || 0]}`,
    // boxSizing: 'border-box'
    background: isEquipped ? '' : `${OUTLINES[props.lines?.length || 0]}`,
    zIndex: 10,
    userSelect: 'none',
    ...(tapMenu ? { touchAction: 'none', WebkitTouchCallout: 'none' } as React.CSSProperties : {}),
    ...style,
  };

  return (
    <div className="item-container" onMouseLeave={onMouseLeave}>
      <div
        className={hidden ? 'item item--hidden' : 'item'}
        ref={ref}
        style={_style}
        onPointerDown={onPointerDown}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={tapMenu ? undefined : _onClick}
        onClick={tapMenu ? _onClick : undefined}
        onMouseEnter={onMouseEnter}
        onMouseUp={tapMenu ? undefined : onMouseUp}
        onContextMenu={onContextMenu}>
        <ImageWithSpinner src={src} style={[161010,161011].includes(item.iid) ? { width: '100%', height: '100%', objectFit: 'contain' } : undefined} />
        {getItemAmount(item) > 1 && (
          <div
            className="text-outline"
            style={{ fontSize: 12, color: 'whitesmoke', position: 'absolute', bottom: 0, right: 2, pointerEvents: 'none' }}>
            x{getItemAmount(item)}
          </div>
        )}

        {(hover || showContextMenu || (draggedItem !== null && isItemType(draggedItem, ItemType.Gem))) && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: width * 26 + (width - 1),
              height: height * 26 + (height - 1),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 4,
            }}>
            {(item.props?.gems ?? []).some((uid: string) => uid !== null) && (
              <div
                style={{
                  background: 'rgba(200, 200, 200, 0.5)',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  zIndex: 0,
                }}></div>
            )}
            {item.props?.gems?.map((gemUID: string, idx: null) => (
              <GemSlot key={`${item.uid}-gem-${idx}-${gemUID}`} uid={gemUID} />
            ))}
          </div>
        )}

        {showSpinner && <Spinner />}

        {hover && <ItemInfo item={item} />}
      </div>

      {showContextMenu && ref.current && (!isEquipped || slotMenu || tapMenu) && (
        <>
          {getItemLocation(item) < 10 ? (
            <ContextMenu
              position={{ x: ref.current.getBoundingClientRect().right, y: ref.current.getBoundingClientRect().y }}
              style={{ color: 'whitesmoke', zIndex: 20, minHeight: height * 26 }}>
              <ContextMenu.Item title="ดูรายละเอียด" onClick={() => {setShowContextMenu(false);setHover(false);setDetails(true);}} />
              {getItemLocation(item) < 10 && (
                <>
                  {EQUIP_ITEM_TYPES.includes(getItemType(item)) && !isEquipped && (
                    <ContextMenu.Item
                      onClick={() => {
                        setShowSpinner(true);
                        toServer('equipItem', { uid: item.uid }, () => {
                          setShowSpinner(false);
                        });
                      }}
                      title="Equip"
                    />
                  )}
                  {([150270, 150271, 150272, 161010,161011].includes(item.iid) || [12, 16, 45, 49, 450].includes(getItemType(item))) && <ContextMenu.Item onClick={(e) => onUse(e)} title="ใช้ทีละอัน" />}
                  {getItemType(item) === ItemType.Box && getItemAmount(item) > 1 && <ContextMenu.Item onClick={(e) => onUse(e, true)} title="เปิดทั้งหมด" />}
                  {tapMenu && <ContextMenu.Item title="ย้ายไอเท็ม" onClick={() => {
                    setShowContextMenu(false);
                    setHidden(true);
                    dispatch(dragItem(item));
                  }} />}
                  {getItemAmount(item) > 1 && <ContextMenu.Item onClick={onSplit} title="Split" />}
                  <ContextMenu.Item onClick={onSell} title="Sell" />
                </>
              )}
              {(item.props?.gems ?? []).some((uid: string) => uid !== null) && (
                <ContextMenu.Item
                  onClick={() => {
                    setShowContextMenu(false);
                    prompt('นำอัญมณีทั้งหมดออกจากอุปกรณ์ ใช้ 1,000 หินต่อครั้ง ยืนยันหรือไม่?', () => {
                      toServer('gemRemoveByUID', item.uid);
                    });
                  }}
                  title="Remove Gems"
                />
              )}
            </ContextMenu>
          ) : (
            <ContextMenu position={{x:ref.current.getBoundingClientRect().right,y:ref.current.getBoundingClientRect().y}} style={{color:'whitesmoke',zIndex:20}}>
              {onPurchase && <ContextMenu.Item title="ซื้อ" onClick={()=>{setShowContextMenu(false);setHover(false);onPurchase();}} />}
              <ContextMenu.Item title="ดูรายละเอียด" onClick={()=>{setShowContextMenu(false);setHover(false);setDetails(true);}} />
              {slotMenu && <ContextMenu.Item title="ย้ายไอเท็ม" onClick={()=>{setShowContextMenu(false);setHover(false);setHidden(true);dispatch(dragItem(item));}}/>}
            </ContextMenu>
          )}
        </>
      )}
      {details && <ItemDetails item={item} onClose={()=>setDetails(false)} />}
    </div>
  );
};

function GemSlot({ uid }: { uid: string | null }) {
  const { character } = React.useContext(CharacterContext);
  let item = useAppSelector((state) => (uid === null ? null : state.inventory.items[uid]));

  if (!!character && !!uid) {
    item = character.items[uid];
  }

  return (
    <div className="grid--overlap" style={{ zIndex: 1 }}>
      <CDNImage src={'ui/Gems/Open.png'} style={{ margin: 'auto' }} />
      {!!item && <CDNImage src={getItemSrc(item)} width={24} height={24} style={{ margin: 'auto' }} />}
    </div>
  );
}
